/**
 * #605 — typing that was never meant for the picker. An app may open a picker
 * on a key press in its own page (a gesture in the app), and a person
 * mid-sentence keeps typing: into the picker's focused field, across its
 * buttons (Tab), then Enter or Space. A burst of typing already going when the
 * picker appeared is CARRIED OVER from the app until the person pauses or uses
 * the pointer — while it lasts, no key answers the picker (Enter in a field,
 * a button's keyboard activation).
 *
 * The bounds: a person who pauses mid-sentence for longer than the pause, then
 * types on, is read as acting — nothing can tell those keys apart; and one
 * who types straight into a freshly opened picker (within the pause) must
 * pause, or click, before a key answers it.
 */
import { useEffect, useMemo, useRef } from 'react'

/** A gap between key presses this long ends a carried burst. */
export const CARRY_PAUSE_MS = 1000

export interface TypingCarry {
  /** Is the typing now still the burst that was going when the picker appeared? */
  carrying(): boolean
}

/** Watches every key and pointer press in the picker's page (capture phase —
 *  before any handler that answers on them). */
export function useTypingCarry(now: () => number = () => Date.now()): TypingCarry {
  // The picker's appearance counts as the burst's last key: a key right after
  // it continues whatever was being typed before. Until a key arrives nothing
  // is carried — a click, or assistive tech's bare click, answers as ever.
  const state = useRef<{ lastKeyAt: number; chain: boolean; keyed: boolean } | null>(null)
  state.current ??= { lastKeyAt: now(), chain: true, keyed: false }
  useEffect(() => {
    const s = state.current!
    const onKey = () => {
      const at = now()
      if (at - s.lastKeyAt > CARRY_PAUSE_MS) s.chain = false
      s.lastKeyAt = at
      s.keyed = true
    }
    // A pointer press is deliberate: whatever was carried is over. (`mousedown`
    // too: some engines' simulated presses — VoiceOver's — send no pointer event.)
    const onPointer = () => { s.chain = false }
    window.addEventListener('keydown', onKey, true)
    window.addEventListener('pointerdown', onPointer, true)
    window.addEventListener('mousedown', onPointer, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('pointerdown', onPointer, true)
      window.removeEventListener('mousedown', onPointer, true)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the clock is fixed for the picker's life
  }, [])
  // Carrying only while the burst still goes on: an activation that sends no
  // key or pointer event (a screen reader's browse-mode Enter, voice control),
  // a pause after the last key, is the person.
  return useMemo(() => ({
    carrying: () => {
      const s = state.current!
      return s.keyed && s.chain && now() - s.lastKeyAt <= CARRY_PAUSE_MS
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the clock is fixed for the picker's life
  }), [])
}
